import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import ScoreCardImage from "./ScoreCardImage";

// ── 레벨 계산 (포인트 → match_level) — 기존 로직 유지 ──────────────────
export function calcMatchLevel(pts) {
  if (!pts || pts < 1)  return 0;
  if (pts <= 3)  return 1;
  if (pts <= 6)  return 2;
  if (pts <= 9)  return 3;
  if (pts <= 12) return 4;
  if (pts <= 15) return 5;
  if (pts <= 18) return 6;
  if (pts <= 21) return 7;
  if (pts <= 24) return 8;
  if (pts <= 27) return 9;
  return 10;
}

// ── 결과 확정 시 양 팀 전적/레벨 업데이트 — SECURITY DEFINER RPC로 처리 ──
// 골프 점수 스왑은 호출부(confirmResult)에서 처리 후 이미 적용된 값이 전달됨
export async function applyResultToTeams(hostTeamId, guestTeamId, hostScore, guestScore) {
  const { error } = await supabase.rpc("apply_match_result", {
    p_host_team_id:  hostTeamId,
    p_guest_team_id: guestTeamId,
    p_host_score:    hostScore,
    p_guest_score:   guestScore,
  });

  if (error) {
    console.error("apply_match_result RPC 오류:", error);
    alert("결과 반영 중 오류가 발생했습니다.");
  }
}

// ══════════════════════════════════════════════════════════════════
// ── 스포츠별 초기 상태
// ══════════════════════════════════════════════════════════════════
function getInitState(sport) {
  if (["soccer", "futsal"].includes(sport))
    return { hostScore: 0, guestScore: 0 };
  if (sport === "basketball")
    return { hostScore: 0, guestScore: 0 };
  if (sport === "baseball")
    return { innings: { host: Array(7).fill(0), guest: Array(7).fill(0) } };
  if (["tennis", "badminton", "tabletennis", "running"].includes(sport))
    return { matchType: "doubles", games: [] };
  if (sport === "bowling" || sport === "golf")
    return { host: [], guest: [] };
  if (sport === "billiards")
    return { matches: [] };
  return { hostScore: 0, guestScore: 0 };
}

// ── 상태 → 최종 점수 계산
function computeScores(sport, state) {
  if (["soccer", "futsal", "basketball"].includes(sport))
    return { hostScore: state.hostScore || 0, guestScore: state.guestScore || 0 };

  if (sport === "baseball") {
    const h = (state.innings?.host || []).reduce((a, b) => a + (parseInt(b) || 0), 0);
    const g = (state.innings?.guest || []).reduce((a, b) => a + (parseInt(b) || 0), 0);
    return { hostScore: h, guestScore: g };
  }

  if (["tennis", "badminton", "tabletennis", "running"].includes(sport)) {
    let hw = 0, gw = 0;
    (state.games || []).forEach((game) => {
      const hSets = (game.sets || []).filter((s) => (parseInt(s.hs) || 0) > (parseInt(s.gs) || 0)).length;
      const gSets = (game.sets || []).filter((s) => (parseInt(s.gs) || 0) > (parseInt(s.hs) || 0)).length;
      if (hSets > gSets) hw++; else if (gSets > hSets) gw++;
    });
    return { hostScore: hw, guestScore: gw };
  }

  if (sport === "bowling" || sport === "golf") {
    const h = (state.host || []).reduce((sum, p) => sum + (parseInt(p.score) || 0), 0);
    const g = (state.guest || []).reduce((sum, p) => sum + (parseInt(p.score) || 0), 0);
    return { hostScore: h, guestScore: g };
  }

  if (sport === "billiards") {
    let hw = 0, gw = 0;
    (state.matches || []).forEach((m) => {
      const hs = parseInt(m.hs) || 0, gs = parseInt(m.gs) || 0;
      if (hs > gs) hw++; else if (gs > hs) gw++;
    });
    return { hostScore: hw, guestScore: gw };
  }

  return { hostScore: 0, guestScore: 0 };
}

// ── 상태 → detail_scores (jsonb)
function buildDetailScores(sport, state) {
  if (["soccer", "futsal", "basketball"].includes(sport)) return null;
  if (sport === "baseball") return state.innings || null;
  if (["tennis", "badminton", "tabletennis", "running"].includes(sport)) {
    return { matchType: state.matchType, games: state.games };
  }
  if (sport === "bowling" || sport === "golf") {
    return [
      ...(state.host || []).map((p) => ({ team: "host",  player: p.name, score: parseInt(p.score) || 0 })),
      ...(state.guest || []).map((p) => ({ team: "guest", player: p.name, score: parseInt(p.score) || 0 })),
    ];
  }
  if (sport === "billiards") {
    return (state.matches || []).map((m, i) => ({
      match: i + 1,
      hPlayer: m.hPlayer, gPlayer: m.gPlayer,
      hs: parseInt(m.hs) || 0, gs: parseInt(m.gs) || 0,
    }));
  }
  return null;
}

// ── 기존 결과 → 편집 상태 복원
function restoreState(sport, result) {
  if (!result) return null;
  const ds = result.detail_scores;

  if (["soccer", "futsal", "basketball"].includes(sport))
    return { hostScore: result.host_score || 0, guestScore: result.guest_score || 0 };

  if (sport === "baseball") {
    if (ds?.host && ds?.guest) return { innings: { host: ds.host, guest: ds.guest } };
    return getInitState(sport);
  }

  if (["tennis", "badminton", "tabletennis", "running"].includes(sport)) {
    if (ds?.games) return { matchType: ds.matchType || "doubles", games: ds.games };
    return getInitState(sport);
  }

  if (sport === "bowling" || sport === "golf") {
    if (Array.isArray(ds)) {
      return {
        host:  ds.filter((p) => p.team === "host").map((p) => ({ name: p.player, score: String(p.score) })),
        guest: ds.filter((p) => p.team === "guest").map((p) => ({ name: p.player, score: String(p.score) })),
      };
    }
    return getInitState(sport);
  }

  if (sport === "billiards") {
    if (Array.isArray(ds)) {
      return { matches: ds.map((m) => ({ hPlayer: m.hPlayer, gPlayer: m.gPlayer, hs: String(m.hs), gs: String(m.gs) })) };
    }
    return getInitState(sport);
  }

  return getInitState(sport);
}

// ── 승/무/패 텍스트
function getOutcomeText(sport, hostScore, guestScore, isHost) {
  const isDraw = hostScore === guestScore;
  if (isDraw) return "draw";
  // 골프: 낮은 점수가 승리
  const hostWins = sport === "golf" ? hostScore < guestScore : hostScore > guestScore;
  return (isHost ? hostWins : !hostWins) ? "win" : "loss";
}

// ══════════════════════════════════════════════════════════════════
// ── 실시간 점수 현황 바
// ══════════════════════════════════════════════════════════════════
function LiveBar({ hostTeamName, guestTeamName, hostScore, guestScore, sport }) {
  const isDraw = hostScore === guestScore;
  const hostLeads = sport === "golf" ? hostScore < guestScore : hostScore > guestScore;
  const guestLeads = sport === "golf" ? guestScore < hostScore : guestScore > hostScore;
  const scoreLabel = sport === "golf" ? "(타수 낮을수록 유리)" : null;

  return (
    <div className="flex items-center bg-gray-50 rounded-2xl p-4 mb-4 border border-gray-100" style={{ width: "100%", boxSizing: "border-box" }}>
      <div className="flex-1 min-w-0 text-center">
        <div className="text-xs text-gray-400 mb-1 truncate">{hostTeamName} <span className="text-gray-300">(홈)</span></div>
        <div className="text-5xl font-black" style={{ color: "#E24B4A" }}>{hostScore}</div>
        {hostLeads && !isDraw && <div className="text-xs font-bold mt-1" style={{ color: "#E24B4A" }}>▲ 우세</div>}
      </div>
      <div className="flex flex-col items-center flex-shrink-0 px-3">
        <div className="text-2xl font-black text-gray-300">VS</div>
        {isDraw && hostScore > 0 && <div className="text-xs text-yellow-500 font-bold">동점</div>}
        {scoreLabel && <div className="text-xs text-gray-400 mt-1 text-center" style={{ fontSize: 9 }}>{scoreLabel}</div>}
      </div>
      <div className="flex-1 min-w-0 text-center">
        <div className="text-xs text-gray-400 mb-1 truncate">{guestTeamName} <span className="text-gray-300">(원정)</span></div>
        <div className="text-5xl font-black" style={{ color: "#185FA5" }}>{guestScore}</div>
        {guestLeads && !isDraw && <div className="text-xs font-bold mt-1" style={{ color: "#185FA5" }}>▲ 우세</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ── 종목별 입력 컴포넌트
// ══════════════════════════════════════════════════════════════════

// ── [A] 축구 / 풋살 (+/- 버튼)
function ScoreCounter({ value, onChange, color }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <button
        className="w-12 h-12 rounded-full text-2xl font-bold flex items-center justify-center active:scale-95 transition-transform"
        style={{ background: color, color: "#fff" }}
        onClick={() => onChange(value + 1)}
      >+</button>
      <div className="text-5xl font-black w-16 text-center tabular-nums" style={{ color }}>{value}</div>
      <button
        className="w-12 h-12 rounded-full text-2xl font-bold flex items-center justify-center border-2 active:scale-95 transition-transform"
        style={{ borderColor: color, color, background: "transparent" }}
        onClick={() => onChange(Math.max(0, value - 1))}
      >−</button>
    </div>
  );
}

function SoccerFutsalInput({ state, onChange }) {
  return (
    <div className="flex items-center justify-center gap-10 py-4">
      <ScoreCounter value={state.hostScore} onChange={(v) => onChange({ ...state, hostScore: v })} color="#E24B4A" />
      <span className="text-4xl font-black text-gray-200">:</span>
      <ScoreCounter value={state.guestScore} onChange={(v) => onChange({ ...state, guestScore: v })} color="#185FA5" />
    </div>
  );
}

// ── [A] 농구 (직접 숫자 입력)
function BasketballInput({ state, onChange, hostTeamName, guestTeamName }) {
  return (
    <div className="flex items-center gap-3 py-4" style={{ width: "100%", boxSizing: "border-box" }}>
      <div className="flex-1 min-w-0 text-center">
        <div className="text-xs text-gray-400 mb-2 truncate">{hostTeamName}</div>
        <input
          type="number" min="0"
          className="w-full text-5xl font-black text-center border-2 rounded-2xl py-3 outline-none"
          style={{ color: "#E24B4A", borderColor: "#E24B4A", minWidth: 0 }}
          placeholder="0"
          value={state.hostScore || ""}
          onChange={(e) => onChange({ ...state, hostScore: Math.max(0, parseInt(e.target.value) || 0) })}
        />
      </div>
      <div className="text-3xl font-black text-gray-200 flex-shrink-0">:</div>
      <div className="flex-1 min-w-0 text-center">
        <div className="text-xs text-gray-400 mb-2 truncate">{guestTeamName}</div>
        <input
          type="number" min="0"
          className="w-full text-5xl font-black text-center border-2 rounded-2xl py-3 outline-none"
          style={{ color: "#185FA5", borderColor: "#185FA5", minWidth: 0 }}
          placeholder="0"
          value={state.guestScore || ""}
          onChange={(e) => onChange({ ...state, guestScore: Math.max(0, parseInt(e.target.value) || 0) })}
        />
      </div>
    </div>
  );
}

// ── [A] 야구 (이닝별)
function BaseballInput({ state, onChange }) {
  const innings = state.innings || { host: Array(7).fill(0), guest: Array(7).fill(0) };

  const addInning = () => onChange({
    innings: { host: [...innings.host, 0], guest: [...innings.guest, 0] },
  });

  const updateInning = (team, idx, val) => {
    const updated = { host: [...innings.host], guest: [...innings.guest] };
    updated[team][idx] = Math.max(0, parseInt(val) || 0);
    onChange({ innings: updated });
  };

  const hostTotal  = innings.host.reduce((a, b) => a + b, 0);
  const guestTotal = innings.guest.reduce((a, b) => a + b, 0);

  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-center text-sm border-collapse">
        <thead>
          <tr>
            <th className="text-xs text-gray-400 font-medium py-1 px-1 text-left w-10">팀</th>
            {innings.host.map((_, i) => (
              <th key={i} className="text-xs text-gray-400 font-medium py-1 px-1 w-8">{i + 1}</th>
            ))}
            <th className="text-xs font-bold text-gray-600 py-1 px-1 w-8">합</th>
          </tr>
        </thead>
        <tbody>
          {[{ key: "host", color: "#E24B4A", label: "홈" }, { key: "guest", color: "#185FA5", label: "원정" }].map(({ key, color, label }) => (
            <tr key={key}>
              <td className="text-xs font-bold py-1 px-1 text-left" style={{ color }}>{label}</td>
              {innings[key].map((v, i) => (
                <td key={i} className="py-1 px-0.5">
                  <input
                    type="number" min="0"
                    className="w-8 h-8 text-center border border-gray-200 rounded text-sm font-medium outline-none"
                    value={v}
                    onChange={(e) => updateInning(key, i, e.target.value)}
                  />
                </td>
              ))}
              <td className="font-black text-base py-1 px-1" style={{ color }}>
                {innings[key].reduce((a, b) => a + b, 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        className="mt-3 w-full py-2 text-xs text-gray-400 border border-dashed border-gray-200 rounded-xl"
        onClick={addInning}
      >+ 이닝 추가</button>
    </div>
  );
}

// ── [B] 라켓 종목 (테니스/배드민턴/탁구/피클볼) — 게임별 세트 스코어
function RacketGame({ game, gameIdx, matchType, hostTeamName, guestTeamName, onChange, onRemove }) {
  const hostSetWins  = (game.sets || []).filter((s) => (parseInt(s.hs) || 0) > (parseInt(s.gs) || 0)).length;
  const guestSetWins = (game.sets || []).filter((s) => (parseInt(s.gs) || 0) > (parseInt(s.hs) || 0)).length;
  const winner = hostSetWins > guestSetWins ? "host" : guestSetWins > hostSetWins ? "guest" : null;

  const addSet    = () => onChange({ ...game, sets: [...(game.sets || []), { hs: 0, gs: 0 }] });
  const removeSet = (si) => onChange({ ...game, sets: game.sets.filter((_, i) => i !== si) });
  const updateSet = (si, field, val) => onChange({
    ...game,
    sets: game.sets.map((s, i) => i === si ? { ...s, [field]: Math.max(0, parseInt(val) || 0) } : s),
  });

  return (
    <div className="border border-gray-100 rounded-2xl p-3 mb-3 bg-gray-50">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-gray-500">제{gameIdx + 1}게임</span>
        {winner && (
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full"
            style={{ background: winner === "host" ? "#fff5f5" : "#eff6ff", color: winner === "host" ? "#E24B4A" : "#185FA5" }}
          >
            {winner === "host" ? `${hostTeamName} 승` : `${guestTeamName} 승`}
          </span>
        )}
        <button className="text-gray-300 text-base leading-none" onClick={onRemove}>✕</button>
      </div>

      {/* 선수 이름 */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <div className="text-xs font-bold mb-1" style={{ color: "#E24B4A" }}>{hostTeamName}</div>
          <input
            className="w-full border border-gray-200 rounded-xl px-2 py-1.5 text-sm outline-none"
            placeholder="선수 이름"
            value={game.h1 || ""}
            onChange={(e) => onChange({ ...game, h1: e.target.value })}
          />
          {matchType === "doubles" && (
            <input
              className="w-full border border-gray-200 rounded-xl px-2 py-1.5 text-sm outline-none mt-1"
              placeholder="선수 2 이름"
              value={game.h2 || ""}
              onChange={(e) => onChange({ ...game, h2: e.target.value })}
            />
          )}
        </div>
        <div>
          <div className="text-xs font-bold mb-1" style={{ color: "#185FA5" }}>{guestTeamName}</div>
          <input
            className="w-full border border-gray-200 rounded-xl px-2 py-1.5 text-sm outline-none"
            placeholder="선수 이름"
            value={game.g1 || ""}
            onChange={(e) => onChange({ ...game, g1: e.target.value })}
          />
          {matchType === "doubles" && (
            <input
              className="w-full border border-gray-200 rounded-xl px-2 py-1.5 text-sm outline-none mt-1"
              placeholder="선수 2 이름"
              value={game.g2 || ""}
              onChange={(e) => onChange({ ...game, g2: e.target.value })}
            />
          )}
        </div>
      </div>

      {/* 세트 스코어 */}
      {(game.sets || []).map((set, si) => (
        <div key={si} className="flex items-center gap-2 mb-1.5" style={{ width: "100%", boxSizing: "border-box" }}>
          <span className="text-xs text-gray-400 flex-shrink-0" style={{ minWidth: 44 }}>세트 {si + 1}</span>
          <input
            type="number" min="0"
            className="flex-1 border border-gray-200 rounded-xl text-center py-1.5 text-sm font-bold outline-none"
            style={{ color: "#E24B4A", minWidth: 0 }}
            value={set.hs}
            onChange={(e) => updateSet(si, "hs", e.target.value)}
          />
          <span className="text-gray-300 font-bold flex-shrink-0">:</span>
          <input
            type="number" min="0"
            className="flex-1 border border-gray-200 rounded-xl text-center py-1.5 text-sm font-bold outline-none"
            style={{ color: "#185FA5", minWidth: 0 }}
            value={set.gs}
            onChange={(e) => updateSet(si, "gs", e.target.value)}
          />
          <button className="text-gray-200 text-sm flex-shrink-0" onClick={() => removeSet(si)}>✕</button>
        </div>
      ))}
      <button
        className="w-full mt-1 py-1.5 text-xs border border-dashed border-gray-200 rounded-xl text-gray-400"
        onClick={addSet}
      >+ 세트 추가</button>
    </div>
  );
}

function RacketInput({ state, onChange, hostTeamName, guestTeamName }) {
  const { matchType = "doubles", games = [] } = state;

  const setMatchType = (t) => onChange({ ...state, matchType: t });

  const addGame = () => onChange({
    ...state,
    games: [...games, { h1: "", h2: "", g1: "", g2: "", sets: [{ hs: 0, gs: 0 }] }],
  });

  const updateGame = (gi, updated) => onChange({
    ...state,
    games: games.map((g, i) => i === gi ? updated : g),
  });

  const removeGame = (gi) => onChange({ ...state, games: games.filter((_, i) => i !== gi) });

  return (
    <div className="mt-3">
      {/* 단식 / 복식 탭 */}
      <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
        {["doubles", "singles"].map((t) => (
          <button
            key={t}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${matchType === t ? "bg-white shadow-sm text-gray-900" : "text-gray-400"}`}
            onClick={() => setMatchType(t)}
          >
            {t === "doubles" ? "복식" : "단식"}
          </button>
        ))}
      </div>

      {games.map((game, gi) => (
        <RacketGame
          key={gi}
          game={game}
          gameIdx={gi}
          matchType={matchType}
          hostTeamName={hostTeamName}
          guestTeamName={guestTeamName}
          onChange={(updated) => updateGame(gi, updated)}
          onRemove={() => removeGame(gi)}
        />
      ))}

      <button
        className="w-full py-2.5 text-sm border border-dashed border-gray-300 rounded-2xl text-gray-400"
        onClick={addGame}
      >+ 게임 추가</button>
    </div>
  );
}

// ── [B] 볼링 & 골프 공용 (선수별 점수)
function PlayerListInput({ state, onChange, hostTeamName, guestTeamName, hostColor, guestColor }) {
  const addPlayer = (team) => onChange({ ...state, [team]: [...(state[team] || []), { name: "", score: "" }] });
  const removePlayer = (team, idx) => onChange({ ...state, [team]: state[team].filter((_, i) => i !== idx) });
  const updatePlayer = (team, idx, field, val) => onChange({
    ...state,
    [team]: state[team].map((p, i) => i === idx ? { ...p, [field]: val } : p),
  });

  return (
    <div className="mt-3">
      {[{ key: "host", label: hostTeamName, color: hostColor }, { key: "guest", label: guestTeamName, color: guestColor }].map(({ key, label, color }) => (
        <div key={key} className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold" style={{ color }}>{label}</span>
            <span className="text-sm font-black" style={{ color }}>
              합계 {(state[key] || []).reduce((sum, p) => sum + (parseInt(p.score) || 0), 0)}
            </span>
          </div>
          {(state[key] || []).map((player, idx) => (
            <div key={idx} className="flex gap-2 mb-2" style={{ width: "100%", boxSizing: "border-box" }}>
              <input
                className="flex-1 min-w-0 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none"
                placeholder="선수 이름"
                value={player.name}
                onChange={(e) => updatePlayer(key, idx, "name", e.target.value)}
              />
              <input
                type="number" min="0"
                className="flex-shrink-0 border border-gray-200 rounded-xl px-2 py-2 text-sm text-center font-bold outline-none"
                style={{ color, width: 64, minWidth: 0 }}
                placeholder="점수"
                value={player.score}
                onChange={(e) => updatePlayer(key, idx, "score", e.target.value)}
              />
              <button className="text-gray-300 text-lg leading-none flex-shrink-0" onClick={() => removePlayer(key, idx)}>✕</button>
            </div>
          ))}
          <button
            className="w-full py-2 text-xs border border-dashed border-gray-200 rounded-xl text-gray-400"
            onClick={() => addPlayer(key)}
          >+ 선수 추가</button>
        </div>
      ))}
    </div>
  );
}

function BowlingInput({ state, onChange, hostTeamName, guestTeamName }) {
  return <PlayerListInput state={state} onChange={onChange} hostTeamName={hostTeamName} guestTeamName={guestTeamName} hostColor="#E24B4A" guestColor="#185FA5" />;
}

function GolfInput({ state, onChange, hostTeamName, guestTeamName }) {
  return (
    <div>
      <div className="text-xs text-center text-amber-600 bg-amber-50 rounded-xl py-2 px-3 mb-2 border border-amber-100">
        ⛳ 골프는 타수가 낮을수록 유리 — 합산 타수가 낮은 팀이 승리로 판정됩니다
      </div>
      <PlayerListInput state={state} onChange={onChange} hostTeamName={hostTeamName} guestTeamName={guestTeamName} hostColor="#E24B4A" guestColor="#185FA5" />
    </div>
  );
}

// ── [B] 당구 (대결별)
function BilliardInput({ state, onChange, hostTeamName, guestTeamName }) {
  const { matches = [] } = state;

  const addMatch    = () => onChange({ ...state, matches: [...matches, { hPlayer: "", gPlayer: "", hs: "", gs: "" }] });
  const removeMatch = (idx) => onChange({ ...state, matches: matches.filter((_, i) => i !== idx) });
  const updateMatch = (idx, field, val) => onChange({
    ...state,
    matches: matches.map((m, i) => i === idx ? { ...m, [field]: val } : m),
  });

  return (
    <div className="mt-3">
      {matches.map((m, idx) => {
        const hs = parseInt(m.hs) || 0, gs = parseInt(m.gs) || 0;
        const winner = hs > gs ? "host" : gs > hs ? "guest" : null;
        return (
          <div key={idx} className="border border-gray-100 rounded-2xl p-3 mb-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-500">제{idx + 1}대결</span>
              {winner && (
                <span className="text-xs font-bold" style={{ color: winner === "host" ? "#E24B4A" : "#185FA5" }}>
                  {winner === "host" ? `${hostTeamName} 승` : `${guestTeamName} 승`}
                </span>
              )}
              <button className="text-gray-300 text-base leading-none" onClick={() => removeMatch(idx)}>✕</button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2" style={{ width: "100%", boxSizing: "border-box" }}>
              <input
                className="w-full border border-gray-200 rounded-xl px-2 py-1.5 text-sm outline-none"
                placeholder="홈 선수 이름"
                value={m.hPlayer}
                onChange={(e) => updateMatch(idx, "hPlayer", e.target.value)}
              />
              <input
                className="w-full border border-gray-200 rounded-xl px-2 py-1.5 text-sm outline-none"
                placeholder="원정 선수 이름"
                value={m.gPlayer}
                onChange={(e) => updateMatch(idx, "gPlayer", e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3" style={{ width: "100%", boxSizing: "border-box" }}>
              <input
                type="number" min="0"
                className="flex-1 border border-gray-200 rounded-xl text-center py-2 text-xl font-black outline-none"
                style={{ color: "#E24B4A", minWidth: 0 }}
                placeholder="0"
                value={m.hs}
                onChange={(e) => updateMatch(idx, "hs", e.target.value)}
              />
              <span className="text-gray-300 font-bold text-xl flex-shrink-0">:</span>
              <input
                type="number" min="0"
                className="flex-1 border border-gray-200 rounded-xl text-center py-2 text-xl font-black outline-none"
                style={{ color: "#185FA5", minWidth: 0 }}
                placeholder="0"
                value={m.gs}
                onChange={(e) => updateMatch(idx, "gs", e.target.value)}
              />
            </div>
          </div>
        );
      })}
      <button
        className="w-full py-2.5 text-sm border border-dashed border-gray-300 rounded-2xl text-gray-400"
        onClick={addMatch}
      >+ 대결 추가</button>
    </div>
  );
}

// ── 종목별 라우터
function ScoreInputRouter({ sport, state, onChange, hostTeamName, guestTeamName }) {
  if (["soccer", "futsal"].includes(sport))
    return <SoccerFutsalInput state={state} onChange={onChange} />;
  if (sport === "basketball")
    return <BasketballInput state={state} onChange={onChange} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />;
  if (sport === "baseball")
    return <BaseballInput state={state} onChange={onChange} />;
  if (["tennis", "badminton", "tabletennis", "running"].includes(sport))
    return <RacketInput state={state} onChange={onChange} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />;
  if (sport === "bowling")
    return <BowlingInput state={state} onChange={onChange} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />;
  if (sport === "golf")
    return <GolfInput state={state} onChange={onChange} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />;
  if (sport === "billiards")
    return <BilliardInput state={state} onChange={onChange} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />;
  // 기본 (알 수 없는 종목)
  return <BasketballInput state={state} onChange={onChange} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />;
}

// ── 확정된 결과 상세 표시
function DetailScoreView({ sport, detailScores, hostTeamName, guestTeamName }) {
  if (!detailScores) return null;

  if (sport === "baseball") {
    const innings = detailScores;
    if (!innings?.host) return null;
    return (
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-center text-xs border-collapse">
          <thead>
            <tr>
              <th className="text-gray-400 font-medium py-1 px-1 text-left">팀</th>
              {innings.host.map((_, i) => <th key={i} className="text-gray-400 font-medium py-1 px-1">{i+1}</th>)}
              <th className="text-gray-600 font-bold py-1 px-1">합</th>
            </tr>
          </thead>
          <tbody>
            {[{ key: "host", color: "#E24B4A", label: "홈" }, { key: "guest", color: "#185FA5", label: "원정" }].map(({ key, color, label }) => (
              <tr key={key}>
                <td className="font-bold py-1 px-1 text-left" style={{ color }}>{label}</td>
                {innings[key].map((v, i) => <td key={i} className="py-1 px-1">{v}</td>)}
                <td className="font-black" style={{ color }}>{innings[key].reduce((a, b) => a + b, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (["tennis", "badminton", "tabletennis", "running"].includes(sport)) {
    const ds = detailScores;
    if (!Array.isArray(ds?.games)) return null;
    return (
      <div className="mt-2 space-y-2">
        {ds.games.map((game, gi) => {
          const hw = game.sets.filter((s) => s.hs > s.gs).length;
          const gw = game.sets.filter((s) => s.gs > s.hs).length;
          const winner = hw > gw ? "host" : gw > hw ? "guest" : null;
          return (
            <div key={gi} className="border border-gray-100 rounded-xl p-2 bg-gray-50 text-xs">
              <div className="flex justify-between mb-1">
                <span className="font-bold text-gray-500">제{gi+1}게임 <span className="text-gray-400">({ds.matchType === "singles" ? "단식" : "복식"})</span></span>
                {winner && <span className="font-bold" style={{ color: winner === "host" ? "#E24B4A" : "#185FA5" }}>{winner === "host" ? `${hostTeamName} 승` : `${guestTeamName} 승`}</span>}
              </div>
              <div className="text-gray-500 mb-1">
                <span style={{ color: "#E24B4A" }}>{[game.h1, game.h2].filter(Boolean).join(" / ") || hostTeamName}</span>
                {" vs "}
                <span style={{ color: "#185FA5" }}>{[game.g1, game.g2].filter(Boolean).join(" / ") || guestTeamName}</span>
              </div>
              <div className="flex gap-1 flex-wrap">
                {game.sets.map((s, si) => (
                  <span key={si} className="bg-white border border-gray-100 rounded px-1.5 py-0.5 text-gray-600">
                    {s.hs}:{s.gs}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (sport === "bowling" || sport === "golf") {
    if (!Array.isArray(detailScores)) return null;
    return (
      <div className="mt-2 grid grid-cols-2 gap-2">
        {[{ key: "host", color: "#E24B4A", label: hostTeamName }, { key: "guest", color: "#185FA5", label: guestTeamName }].map(({ key, color, label }) => {
          const players = detailScores.filter((p) => p.team === key);
          return (
            <div key={key} className="bg-gray-50 rounded-xl p-2 text-xs">
              <div className="font-bold mb-1 truncate" style={{ color }}>{label}</div>
              {players.map((p, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-gray-500 truncate">{p.player}</span>
                  <span className="font-bold ml-1" style={{ color }}>{p.score}</span>
                </div>
              ))}
              <div className="border-t border-gray-200 mt-1 pt-1 flex justify-between font-black">
                <span className="text-gray-500">합계</span>
                <span style={{ color }}>{players.reduce((s, p) => s + p.score, 0)}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (sport === "billiards") {
    if (!Array.isArray(detailScores)) return null;
    return (
      <div className="mt-2 space-y-1.5">
        {detailScores.map((m, i) => {
          const winner = m.hs > m.gs ? "host" : m.gs > m.hs ? "guest" : null;
          return (
            <div key={i} className="flex items-center gap-2 text-xs bg-gray-50 rounded-xl px-3 py-2">
              <span className="text-gray-400 w-12">제{i+1}대결</span>
              <span className="flex-1 truncate" style={{ color: "#E24B4A" }}>{m.hPlayer}</span>
              <span className="font-black" style={{ color: "#E24B4A" }}>{m.hs}</span>
              <span className="text-gray-300">:</span>
              <span className="font-black" style={{ color: "#185FA5" }}>{m.gs}</span>
              <span className="flex-1 text-right truncate" style={{ color: "#185FA5" }}>{m.gPlayer}</span>
              {winner && (
                <span className="ml-1 font-bold" style={{ color: winner === "host" ? "#E24B4A" : "#185FA5", fontSize: 9 }}>
                  {winner === "host" ? `${hostTeamName}승` : `${guestTeamName}승`}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return null;
}

// ══════════════════════════════════════════════════════════════════
// ── 메인 컴포넌트
// ══════════════════════════════════════════════════════════════════
export default function MatchResult({
  app, myTeam, hostTeamName, guestTeamName, sportsType, onClose, onDone,
}) {
  const sport   = sportsType || "soccer";
  const isHost  = myTeam.id === app.host_team_id;

  const [result,         setResult]         = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [showCardImage,  setShowCardImage]  = useState(false);
  const [error,     setError]     = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [scoreState, setScoreState] = useState(() => getInitState(sport));

  // ── 기존 결과 로드
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("match_results").select("*").eq("match_id", app.id).maybeSingle();
      setResult(data || null);
      if (data) {
        const restored = restoreState(sport, data);
        if (restored) setScoreState(restored);
      }
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.id]);

  const { hostScore, guestScore } = computeScores(sport, scoreState);

  // 내 확인 여부 & 상대 확인 여부
  const myConfirmed  = result ? (isHost ? (result.host_confirmed ?? false) : (result.guest_confirmed ?? false)) : false;
  const oppConfirmed = result ? (isHost ? (result.guest_confirmed ?? false) : (result.host_confirmed ?? false)) : false;

  // ── 제출 (신규 insert)
  const submitResult = async () => {
    setSaving(true); setError("");
    const hs = hostScore, gs = guestScore;
    const ds = buildDetailScores(sport, scoreState);

    const { data: saved, error: saveErr } = await supabase
      .from("match_results")
      .insert([{
        match_id:       app.id,
        host_team_id:   app.host_team_id,
        guest_team_id:  app.guest_team_id,
        host_score:     hs,
        guest_score:    gs,
        detail_scores:  ds,
        result_status:  "pending",
        submitted_by:   myTeam.id,
        host_confirmed:  isHost,
        guest_confirmed: !isHost,
      }])
      .select().single();

    if (saveErr) { setError("저장 오류: " + saveErr.message); setSaving(false); return; }

    const opponentId = isHost ? app.guest_team_id : app.host_team_id;
    await supabase.from("notifications").insert([{
      team_id:    opponentId,
      type:       "result_confirm_request",
      title:      "📊 경기 결과 확인 요청",
      body:       `${myTeam.team_name} 팀이 경기 결과를 입력했어요. 확인해주세요!`,
      related_id: app.id,
    }]);

    setResult(saved);
    setSaving(false);
    onDone?.();
  };

  // ── 수정 (update + 상대 confirmed 초기화)
  const updateResult = async () => {
    setSaving(true); setError("");
    const hs = hostScore, gs = guestScore;
    const ds = buildDetailScores(sport, scoreState);

    const { data: updated, error: updateErr } = await supabase
      .from("match_results")
      .update({
        host_score:      hs,
        guest_score:     gs,
        detail_scores:   ds,
        host_confirmed:  isHost,
        guest_confirmed: !isHost,
      })
      .eq("id", result.id)
      .select().single();

    if (updateErr) { setError("수정 오류: " + updateErr.message); setSaving(false); return; }

    const opponentId = isHost ? app.guest_team_id : app.host_team_id;
    await supabase.from("notifications").insert([{
      team_id:    opponentId,
      type:       "result_confirm_request",
      title:      "📊 경기 결과 수정됨",
      body:       `${myTeam.team_name} 팀이 결과를 수정했어요. 다시 확인해주세요!`,
      related_id: app.id,
    }]);

    setResult(updated);
    setIsEditing(false);
    setSaving(false);
    onDone?.();
  };

  // ── 확인 (내 confirmed = true → 양쪽 true면 최종 확정)
  const confirmResult = async () => {
    setSaving(true); setError("");

    const newHostConfirmed  = isHost  ? true : (result.host_confirmed  ?? false);
    const newGuestConfirmed = !isHost ? true : (result.guest_confirmed ?? false);
    const bothConfirmed     = newHostConfirmed && newGuestConfirmed;

    const updateFields = {
      ...(isHost  ? { host_confirmed: true }  : {}),
      ...(!isHost ? { guest_confirmed: true } : {}),
      ...(bothConfirmed ? {
        result_status: "confirmed",
        confirmed_by:  myTeam.id,
        confirmed_at:  new Date().toISOString(),
      } : {}),
    };

    const { error: confErr } = await supabase
      .from("match_results").update(updateFields).eq("id", result.id);

    if (confErr) { setError("확인 오류: " + confErr.message); setSaving(false); return; }

    if (bothConfirmed) {
      // 골프는 낮은 점수가 승리 → applyResultToTeams에 역전해서 전달
      const effectiveHost  = sport === "golf" ? result.guest_score : result.host_score;
      const effectiveGuest = sport === "golf" ? result.host_score  : result.guest_score;
      await applyResultToTeams(app.host_team_id, app.guest_team_id, effectiveHost, effectiveGuest);

      const notice = "경기 결과가 확인됐어요! 48시간 내 매너 평가를 완료해주세요";
      await Promise.all([
        supabase.from("notifications").insert([{ team_id: app.host_team_id, type: "manner_review_request", title: "⭐ 매너 평가를 해주세요", body: notice, related_id: app.id }]),
        supabase.from("notifications").insert([{ team_id: app.guest_team_id, type: "manner_review_request", title: "⭐ 매너 평가를 해주세요", body: notice, related_id: app.id }]),
        supabase.from("notifications").insert([{ team_id: app.host_team_id, type: "result_confirmed", title: "✅ 경기 결과 확인됨", body: `${myTeam.team_name} 팀이 경기 결과를 확인했어요`, related_id: app.id }]),
      ]);
    }

    setResult((prev) => ({ ...prev, ...updateFields }));
    setSaving(false);
    onDone?.();
  };

  // ── 수정 모드 진입
  const enterEditMode = () => {
    const restored = restoreState(sport, result);
    if (restored) setScoreState(restored);
    setIsEditing(true);
  };

  // ── 로딩
  if (loading) {
    return (
      <div style={s.backdrop} onClick={onClose}>
        <div style={s.sheet} onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-center py-16">
            <div style={s.spinner} />
          </div>
        </div>
      </div>
    );
  }

  const outcome = result?.result_status === "confirmed"
    ? getOutcomeText(sport, result.host_score, result.guest_score, isHost)
    : null;

  // ── 확정된 결과 표시
  if (result?.result_status === "confirmed") {
    return (
      <>
        <div style={s.backdrop} onClick={onClose}>
          <div style={s.sheet} onClick={(e) => e.stopPropagation()}>
            <SheetHeader title="📊 경기 결과" onClose={onClose} />
            <VsBanner hostTeamName={hostTeamName} guestTeamName={guestTeamName} />
            <div style={s.body}>
              <div className="text-xs font-bold text-green-600 mb-3">✅ 확인된 경기 결과</div>
              <LiveBar
                hostTeamName={hostTeamName} guestTeamName={guestTeamName}
                hostScore={result.host_score} guestScore={result.guest_score}
                sport={sport}
              />
              <DetailScoreView sport={sport} detailScores={result.detail_scores} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />
              <OutcomeBox outcome={outcome} teamName={myTeam.team_name} showPoints />
              <button
                style={{ ...s.primaryBtn, marginTop: 16, width: "100%" }}
                onClick={() => setShowCardImage(true)}
              >
                📷 결과 이미지 저장
              </button>
              <button style={{ ...s.secondaryBtn, marginTop: 8, width: "100%" }} onClick={onClose}>닫기</button>
            </div>
          </div>
        </div>
        {showCardImage && (
          <ScoreCardImage
            match={app?.post || null}
            hostTeamName={hostTeamName}
            guestTeamName={guestTeamName}
            hostScore={result.host_score}
            guestScore={result.guest_score}
            detailScores={result.detail_scores}
            sportsType={sport}
            onClose={() => setShowCardImage(false)}
          />
        )}
      </>
    );
  }

  // ── 입력 폼 (신규 or 편집 모드)
  const showInputForm = !result || isEditing;
  if (showInputForm) {
    const previewOutcome = hostScore !== 0 || guestScore !== 0
      ? getOutcomeText(sport, hostScore, guestScore, isHost)
      : null;

    return (
      <div style={s.backdrop} onClick={onClose}>
        <div style={s.sheet} onClick={(e) => e.stopPropagation()}>
          <SheetHeader title={isEditing ? "✏️ 결과 수정" : "📊 결과 입력"} onClose={onClose} />
          <VsBanner hostTeamName={hostTeamName} guestTeamName={guestTeamName} />
          <div style={s.body}>
            {/* 실시간 현황 바 */}
            <LiveBar
              hostTeamName={hostTeamName} guestTeamName={guestTeamName}
              hostScore={hostScore} guestScore={guestScore}
              sport={sport}
            />

            {/* 종목별 입력 UI */}
            <ScoreInputRouter
              sport={sport}
              state={scoreState}
              onChange={setScoreState}
              hostTeamName={hostTeamName}
              guestTeamName={guestTeamName}
            />

            {/* 미리보기 결과 */}
            {previewOutcome && (
              <div className="mt-3">
                <OutcomeBox outcome={previewOutcome} teamName={myTeam.team_name} showPoints />
              </div>
            )}

            {error && <p className="text-xs text-red-500 mt-2">{error}</p>}

            <div className="flex gap-2 mt-4">
              {isEditing && (
                <button
                  style={s.secondaryBtn}
                  onClick={() => setIsEditing(false)}
                >취소</button>
              )}
              <button
                style={{ ...s.primaryBtn, opacity: saving ? 0.6 : 1, flex: 1 }}
                onClick={isEditing ? updateResult : submitResult}
                disabled={saving}
              >
                {saving ? "저장 중..." : isEditing ? "수정 완료" : "결과 제출하기"}
              </button>
            </div>
            <p style={s.hintText}>제출 후 상대 팀이 확인해야 최종 반영돼요</p>
          </div>
        </div>
      </div>
    );
  }

  // ── 대기 중 (내가 제출, 상대 확인 대기)
  if (result?.result_status === "pending" && myConfirmed && !oppConfirmed) {
    return (
      <div style={s.backdrop} onClick={onClose}>
        <div style={s.sheet} onClick={(e) => e.stopPropagation()}>
          <SheetHeader title="📊 경기 결과" onClose={onClose} />
          <VsBanner hostTeamName={hostTeamName} guestTeamName={guestTeamName} />
          <div style={s.body}>
            <div className="text-xs font-bold text-gray-500 mb-3">제출한 결과 (상대 팀 확인 대기 중)</div>
            <LiveBar
              hostTeamName={hostTeamName} guestTeamName={guestTeamName}
              hostScore={result.host_score} guestScore={result.guest_score}
              sport={sport}
            />
            <DetailScoreView sport={sport} detailScores={result.detail_scores} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />
            <div className="mt-3 text-center py-3 bg-amber-50 rounded-xl border border-amber-100 text-amber-700 text-sm font-medium">
              ⏳ 상대 팀의 확인을 기다리고 있어요
            </div>
            <button style={s.secondaryBtn} className="w-full mt-3" onClick={enterEditMode}>
              ✏️ 결과 수정하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── 확인 요청 (상대가 제출, 내가 확인해야 함)
  if (result?.result_status === "pending" && !myConfirmed && oppConfirmed) {
    const oppOutcome = getOutcomeText(sport, result.host_score, result.guest_score, isHost);
    return (
      <div style={s.backdrop} onClick={onClose}>
        <div style={s.sheet} onClick={(e) => e.stopPropagation()}>
          <SheetHeader title="📊 경기 결과 확인" onClose={onClose} />
          <VsBanner hostTeamName={hostTeamName} guestTeamName={guestTeamName} />
          <div style={s.body}>
            <div className="text-xs font-bold text-gray-500 mb-3">상대 팀이 입력한 결과</div>
            <LiveBar
              hostTeamName={hostTeamName} guestTeamName={guestTeamName}
              hostScore={result.host_score} guestScore={result.guest_score}
              sport={sport}
            />
            <DetailScoreView sport={sport} detailScores={result.detail_scores} hostTeamName={hostTeamName} guestTeamName={guestTeamName} />
            <div className="mt-3">
              <OutcomeBox outcome={oppOutcome} teamName={myTeam.team_name} />
            </div>
            {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
            <div className="flex gap-2 mt-4">
              <button
                style={{ ...s.secondaryBtn, opacity: saving ? 0.6 : 1 }}
                onClick={enterEditMode}
                disabled={saving}
              >수정하기</button>
              <button
                style={{ ...s.primaryBtn, flex: 1, opacity: saving ? 0.6 : 1 }}
                onClick={confirmResult}
                disabled={saving}
              >{saving ? "확인 중..." : "✅ 결과 확인"}</button>
            </div>
            <p style={s.hintText}>확인하면 포인트와 전적이 자동 반영돼요</p>
          </div>
        </div>
      </div>
    );
  }

  // ── fallback (예외 상황)
  return null;
}

// ── 공용 하위 컴포넌트
function SheetHeader({ title, onClose }) {
  return (
    <div style={s.sheetHeader}>
      <div style={s.sheetTitle}>{title}</div>
      <button style={s.closeBtn} onClick={onClose}>✕</button>
    </div>
  );
}

function VsBanner({ hostTeamName, guestTeamName }) {
  return (
    <div style={s.vsRow}>
      <div style={s.vsTeam}><div style={s.vsTeamName}>{hostTeamName}</div><div style={s.vsRoleLabel}>홈</div></div>
      <div style={s.vsText}>VS</div>
      <div style={s.vsTeam}><div style={s.vsTeamName}>{guestTeamName}</div><div style={s.vsRoleLabel}>원정</div></div>
    </div>
  );
}

function OutcomeBox({ outcome, teamName, showPoints }) {
  if (!outcome) return null;
  const colors = {
    win:  { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a" },
    loss: { background: "#fff5f5", border: "1px solid #fecaca", color: "#E24B4A" },
    draw: { background: "#fffbeb", border: "1px solid #fde68a", color: "#d97706" },
  };
  return (
    <div style={{ ...s.outcomeBox, ...(colors[outcome] || {}) }}>
      {outcome === "win"  && `🏆 ${teamName} 승리${showPoints ? " (+1점 획득)" : ""}`}
      {outcome === "loss" && `💔 ${teamName} 패배${showPoints ? " (-1점)" : ""}`}
      {outcome === "draw" && `🤝 무승부${showPoints ? " (+0.5점 획득)" : ""}`}
    </div>
  );
}

// ── 스타일 (backdrop / sheet 등 모달 구조만 인라인)
const s = {
  backdrop:    { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, backdropFilter: "blur(4px)" },
  sheet:       { width: "92%", maxWidth: 400, background: "#fff", borderRadius: 22, maxHeight: "88vh", overflowY: "auto", overflowX: "hidden", boxSizing: "border-box", boxShadow: "0 8px 40px rgba(0,0,0,0.22)" },
  sheetHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 10px" },
  sheetTitle:  { fontSize: 17, fontWeight: 700, color: "#111827" },
  closeBtn:    { background: "none", border: "none", fontSize: 18, color: "#9ca3af", cursor: "pointer", padding: "4px 8px" },
  vsRow:       { display: "flex", alignItems: "center", justifyContent: "space-around", padding: "10px 20px 14px", borderBottom: "1px solid #f0f1f5" },
  vsTeam:      { textAlign: "center", flex: 1 },
  vsTeamName:  { fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110 },
  vsRoleLabel: { fontSize: 11, color: "#9ca3af" },
  vsText:      { fontSize: 20, fontWeight: 800, color: "#E24B4A", margin: "0 8px", flexShrink: 0 },
  body:        { padding: "14px 20px 36px", width: "100%", boxSizing: "border-box", overflowX: "hidden" },
  outcomeBox:  { textAlign: "center", padding: "12px 16px", borderRadius: 12, fontSize: 14, fontWeight: 700, marginTop: 12 },
  primaryBtn:  { padding: "14px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "block", textAlign: "center" },
  secondaryBtn:{ padding: "12px 20px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  hintText:    { fontSize: 11, color: "#9ca3af", textAlign: "center", marginTop: 10, lineHeight: 1.6 },
  spinner:     { width: 32, height: 32, border: "3px solid #e5e7eb", borderTop: "3px solid #E24B4A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};
